import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Save,
  X,
  AlertCircle,
  ArrowLeft,
  Plus,
  Trash2,
  CheckCircle,
  BookOpen,
  MessageSquare,
  Edit3,
  FileText,
  Loader2,
  TrendingUp,
  Tag,
  Hash,
  Star,
  Zap,
  ShieldAlert,
  GitBranch,
} from 'lucide-react';
import Swal from 'sweetalert2';
import { pdfAnalysisService } from '../../services/pdf-analysis.service';
import { VersionHistoryModal } from '../../components/versioning/VersionHistoryModal';
import { RichTextEditor } from '../../components/editorial/RichTextEditor';

// Rich-text content helpers (client request 2026-07: formatting toolbar in
// the bank's Edit screen). Existing questions hold plain text — convert to
// simple HTML paragraphs for the editor; edited fields are saved as HTML
// (the backend sanitizes to the same whitelist as the review editor).
function escapeHtmlText(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function toEditorHtml(value: string | undefined | null): string {
  if (!value) return '';
  if (/<[a-z][^>]*>/i.test(value)) return value; // already HTML
  return value
    .split(/\n{2,}/)
    .map((block) => `<p>${escapeHtmlText(block).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

const TYPE_OPTIONS = [
  { value: 'multiple_choice', label: 'Multiple Choice (MCQ)', icon: CheckCircle },
  { value: 'short_answer', label: 'Short Answer', icon: Edit3 },
  { value: 'case_study', label: 'Case Study', icon: BookOpen },
  { value: 'interactive', label: 'Interactive', icon: MessageSquare },
];

const DIFFICULTY_OPTIONS = ['easy', 'medium', 'hard'];
const COGNITIVE_OPTIONS = ['knowledge', 'comprehension', 'application', 'analysis'];
const STATUS_OPTIONS = ['generated', 'draft', 'under_review', 'approved', 'rejected'];
const MCQ_FORMAT_OPTIONS = [
  { value: 'anbima', label: 'ANBIMA' },
  { value: 'non-anbima', label: 'Non-ANBIMA' },
];

// Type-specific "AI Wrong" reason options
const AI_WRONG_OPTIONS: Record<string, string[]> = {
  multiple_choice: [
    'Wrong correct answer marked',
    'Calculation error in stem',
    'Distractor too obvious',
    'Distractor too similar to correct',
    'Stem is unclear or ambiguous',
    'Explanation contradicts answer',
    'Options don\'t match stem context',
    'Fabricated data / numbers',
    'Language / grammar issue',
  ],
  case_study: [
    'Case context has fabricated data',
    'Case data doesn\'t match question',
    'Wrong correct answer',
    'Calculation error',
    'Missing important case details',
    'Distractor too obvious',
    'Explanation contradicts answer',
    'Context too vague for question',
    'Language / grammar issue',
  ],
  interactive: [
    'Wrong initial context',
    'Prompt text is unclear',
    'Wrong scoring on alternatives',
    'Incorrect optimal path',
    'Missing decision branches',
    'Alternative doesn\'t make sense',
    'Explanation contradicts scoring',
    'Fabricated scenario data',
    'Language / grammar issue',
  ],
  short_answer: [
    'Wrong expected answer',
    'Rubric criteria is incorrect',
    'Stem is unclear or ambiguous',
    'Calculation error',
    'Explanation contradicts answer',
    'Too vague for short answer format',
    'Fabricated data / numbers',
    'Language / grammar issue',
  ],
};

export function PdfQuestionEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Form state
  const [form, setForm] = useState<any>({});
  const [dirty, setDirty] = useState(false);

  // Question versioning (NQG-RQ-27)
  const [showVersions, setShowVersions] = useState(false);

  // Fetch question
  const { data: question, isLoading, error: fetchError } = useQuery({
    queryKey: ['pdf-generated-question', id],
    queryFn: () => pdfAnalysisService.getGeneratedQuestion(id!),
    enabled: !!id,
  });

  // When the loaded question IS a version, fetch its version tree so the
  // banner can show the exact label (Q1.N) and category (NQG-RQ-27).
  const { data: versionTree } = useQuery({
    queryKey: ['question-versions', id],
    queryFn: () => pdfAnalysisService.getQuestionVersions(id!),
    enabled: !!id && !!question?.rootQuestionId,
  });
  const versionEntry = versionTree?.history?.find((h) => h.questionId === id);

  // Normalize type value from DB to match our select options
  const normalizeType = (t: string) => {
    const map: Record<string, string> = {
      MCQ: 'multiple_choice',
      mcq: 'multiple_choice',
      SHORT: 'short_answer',
      short: 'short_answer',
      CASE: 'case_study',
      INTERACTIVE: 'interactive',
    };
    return map[t] || t;
  };

  // Load question into form
  useEffect(() => {
    if (question) {
      setForm({ ...question, type: normalizeType(question.type || 'multiple_choice') });
      setDirty(false);
    }
  }, [question]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: any) => pdfAnalysisService.updateGeneratedQuestion(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pdf-generated-question', id] });
      queryClient.invalidateQueries({ queryKey: ['pdf-generated-questions'] });
      setDirty(false);
      Swal.fire({
        title: 'Saved!',
        text: 'Question updated successfully.',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      });
    },
    onError: (err: any) => {
      Swal.fire({
        title: 'Save Failed',
        text: err.response?.data?.error || err.message || 'Failed to save.',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    },
  });

  const updateField = (key: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  // AI Wrong flow
  const onAiWrongClick = async () => {
    const qType = form.type || 'multiple_choice';
    const reasons = AI_WRONG_OPTIONS[qType] || AI_WRONG_OPTIONS.multiple_choice;
    const typeLabel = TYPE_OPTIONS.find((t) => t.value === qType)?.label || qType;

    const checkboxesHtml = reasons
      .map(
        (r) =>
          `<label class="flex items-center gap-3 py-3 px-4 rounded-xl border-2 border-transparent hover:border-red-200 hover:bg-gradient-to-r hover:from-red-50 hover:to-rose-50 cursor-pointer transition-all duration-200 group">
            <input type="checkbox" value="${r}" class="ai-wrong-cb w-5 h-5 accent-red-500 rounded-md flex-shrink-0 cursor-pointer" />
            <span class="text-sm text-gray-700 group-hover:text-red-700 font-medium transition-colors leading-snug">${r}</span>
          </label>`
      )
      .join('');

    const result = await Swal.fire({
      html: `
        <div class="text-left">
          <!-- Header -->
          <div class="bg-gradient-to-br from-red-50 via-rose-50 to-orange-50 rounded-2xl p-5 mb-5 border-2 border-red-100">
            <div class="flex items-center gap-3 mb-2">
              <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shadow-lg">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="text-white"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
              </div>
              <div class="flex-1">
                <h2 class="text-xl font-bold text-gray-900 mb-0.5">Report AI Issue</h2>
                <p class="text-xs font-medium text-gray-500">Help us improve question quality</p>
              </div>
            </div>
            <div class="flex items-center gap-2 mt-3">
              <span class="text-[10px] font-bold text-red-600 uppercase tracking-wider">Question Type:</span>
              <span class="px-2.5 py-1 rounded-lg bg-white/80 backdrop-blur-sm text-xs font-bold text-gray-700 border border-red-100">${typeLabel}</span>
            </div>
          </div>

          <!-- Instructions -->
          <div class="mb-3">
            <p class="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-red-500"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
              Select all issues that apply
            </p>
          </div>

          <!-- Issues List -->
          <div class="max-h-72 overflow-y-auto space-y-1 rounded-xl border-2 border-gray-200 bg-gradient-to-b from-gray-50 to-white p-3 shadow-inner">
            ${checkboxesHtml}
          </div>

          <!-- Other Option -->
          <div class="mt-4 p-3 rounded-xl bg-gray-50 border border-gray-200">
            <label class="flex items-center gap-3 cursor-pointer group">
              <input type="checkbox" id="ai-wrong-other-check" class="ai-wrong-cb w-[18px] h-[18px] accent-red-500 rounded-md flex-shrink-0" value="__OTHER__" onchange="var inp=this.closest('.text-left').querySelector('#ai-wrong-other-input'); inp.style.display=this.checked?'block':'none'; if(this.checked) inp.focus();" />
              <span class="text-sm font-semibold text-gray-700 group-hover:text-red-600 transition-colors">Other (specify below)</span>
            </label>
            <input id="ai-wrong-other-input" type="text" placeholder="Describe the specific issue..." class="hidden mt-3 w-full text-sm border-2 border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all placeholder-gray-400" />
          </div>

          <!-- Helper Text -->
          <div class="mt-4 flex items-start gap-2 text-xs text-gray-500 bg-blue-50 rounded-xl p-3 border border-blue-100">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-blue-500 flex-shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
            <p class="leading-relaxed">Your feedback helps train our AI to generate better questions. All reports are reviewed by the team.</p>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Submit Report',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      customClass: {
        popup: 'rounded-3xl !w-[560px] !p-6',
        htmlContainer: '!px-0 !mx-0',
        actions: '!mt-6 !gap-3',
        confirmButton: '!rounded-xl !text-sm !font-bold !px-8 !py-3 !shadow-lg hover:!shadow-xl !transition-all',
        cancelButton: '!rounded-xl !text-sm !font-semibold !px-6 !py-3',
      },
      preConfirm: () => {
        const popup = Swal.getPopup();
        if (!popup) return false;
        const checked = Array.from(
          popup.querySelectorAll<HTMLInputElement>('.ai-wrong-cb:checked')
        ).map((el) => el.value);

        // Replace __OTHER__ with the actual text
        const otherInput = popup.querySelector('#ai-wrong-other-input') as HTMLInputElement;
        const otherIdx = checked.indexOf('__OTHER__');
        if (otherIdx !== -1) {
          const otherText = otherInput?.value?.trim();
          if (otherText) {
            checked[otherIdx] = otherText;
          } else {
            checked.splice(otherIdx, 1);
          }
        }

        if (checked.length === 0) {
          Swal.showValidationMessage('Select at least one reason');
          return false;
        }
        return checked;
      },
    });

    if (!result.isConfirmed || !result.value) return;

    try {
      await pdfAnalysisService.reportAiWrong(qType, result.value);
      await pdfAnalysisService.updateGeneratedQuestion(id!, { validationStatus: 'ai_wrong' });
      queryClient.invalidateQueries({ queryKey: ['pdf-generated-question', id] });
      Swal.fire({
        html: `
          <div class="text-center py-4">
            <div class="w-20 h-20 rounded-2xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mx-auto mb-4 shadow-xl animate-bounce-once">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="text-white"><path d="M20 6 9 17l-5-5"/></svg>
            </div>
            <h2 class="text-2xl font-bold text-gray-900 mb-2">Report Submitted!</h2>
            <p class="text-sm text-gray-600 mb-1">Thank you for your feedback</p>
            <div class="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-green-50 border border-green-200 mt-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-green-600"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/></svg>
              <span class="text-sm font-bold text-green-700">${result.value.length} issue${result.value.length > 1 ? 's' : ''} reported</span>
            </div>
          </div>
        `,
        showConfirmButton: false,
        timer: 2000,
        customClass: { popup: 'rounded-3xl !w-[380px] !p-8' },
      });
    } catch (err: any) {
      Swal.fire({ title: 'Error', text: err.message || 'Failed to save.', icon: 'error', confirmButtonColor: '#dc2626' });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.stem?.trim()) {
      Swal.fire({ title: 'Validation', text: 'Question stem is required.', icon: 'warning', confirmButtonColor: '#1e73be' });
      return;
    }

    // Build update payload — only changed fields
    const payload: any = {
      stem: form.stem,
      type: form.type,
      difficulty: form.difficulty,
      status: form.status,
      explanation: form.explanation || '',
      correctAnswer: form.correctAnswer || '',
      oeCode: form.oeCode || '',
      cognitiveLevel: form.cognitiveLevel || '',
      hasCalculation: form.hasCalculation || false,
      estimatedTime: form.estimatedTime || 180,
      alternatives: form.alternatives || [],
      keywords: form.keywords || [],
      tags: form.tags || [],
      mcqFormat: form.mcqFormat || undefined,
    };

    // Type-specific fields
    if (form.type === 'case_study') {
      payload.caseTitle = form.caseTitle || '';
      payload.caseContext = form.caseContext || '';
      payload.caseData = form.caseData || {};
      payload.subQuestions = form.subQuestions || [];
      payload.isCaseParent = true;
      payload.caseQuestionNumber = (form.subQuestions || []).length;
    }
    if (form.type === 'interactive') {
      payload.initialContext = form.initialContext || '';
      payload.prompts = form.prompts || [];
      payload.optimalPath = form.optimalPath || [];
    }
    if (form.type === 'short_answer') {
      payload.rubric = form.rubric || {};
    }

    saveMutation.mutate(payload);
  };

  const goBack = () => {
    if (dirty) {
      Swal.fire({
        title: 'Unsaved Changes',
        text: 'You have unsaved changes. Discard?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Discard',
        cancelButtonText: 'Keep Editing',
        confirmButtonColor: '#dc2626',
      }).then((r) => { if (r.isConfirmed) navigate('/pdf-analysis/question-bank'); });
    } else {
      navigate('/pdf-analysis/question-bank');
    }
  };

  // Loading
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin text-[#1e73be]" />
          <span className="text-sm font-medium">Loading question...</span>
        </div>
      </div>
    );
  }

  // Error
  if (fetchError || !question) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Question Not Found</h2>
          <p className="text-gray-600 mb-4">ID: {id}</p>
          <button onClick={() => navigate('/pdf-analysis/question-bank')} className="btn-primary px-6 py-2.5 rounded-xl">
            Back to Question Bank
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={goBack} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Question</h1>
            <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-2">
              <span className="font-mono text-[#1e73be]">{form.oeCode}</span>
              {form.mcqFormat && (
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                  form.mcqFormat === 'non-anbima'
                    ? 'bg-orange-100 text-orange-700 border border-orange-200'
                    : 'bg-blue-100 text-blue-700 border border-blue-200'
                }`}>
                  {form.mcqFormat}
                </span>
              )}
              {form.sourceDocuments?.[0]?.filename && (
                <span className="text-gray-400">from {form.sourceDocuments[0].filename}</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={goBack} className="btn-secondary flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold">
            <X className="w-4 h-4" />
            Cancel
          </button>
          <button
            type="button"
            onClick={() => setShowVersions(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-purple-50 text-purple-700 border-2 border-purple-200 hover:border-purple-300 hover:bg-purple-100 transition-all"
          >
            <GitBranch className="w-4 h-4" />
            Versions
          </button>
          <button
            type="button"
            onClick={onAiWrongClick}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-red-50 to-rose-50 text-red-600 border-2 border-red-300 hover:border-red-400 hover:from-red-100 hover:to-rose-100 hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
          >
            <ShieldAlert className="w-4 h-4" />
            Report Issue
          </button>
          <button
            onClick={handleSubmit}
            disabled={saveMutation.isPending || !dirty}
            className="btn-primary flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Version banner (NQG-RQ-27) — shown when this question IS a version */}
      {question?.rootQuestionId && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-purple-50 border-2 border-purple-200 rounded-xl">
          <div className="flex items-center gap-2 min-w-0">
            <GitBranch className="w-4 h-4 text-purple-600 flex-shrink-0" />
            <span className="text-sm font-semibold text-purple-800 truncate">
              Version {versionEntry?.label || `Q1.${question.versionNumber || ''}`}
              {versionEntry?.categoryLabel ? ` (${versionEntry.categoryLabel})` : ''}
            </span>
          </div>
          <button
            type="button"
            onClick={() => navigate(`/pdf-analysis/questions/${question.rootQuestionId}/edit`)}
            className="text-xs font-semibold text-purple-700 hover:text-purple-900 underline flex-shrink-0"
          >
            View original
          </button>
        </div>
      )}

      {/* Version History Modal (NQG-RQ-27) — works for originals and versions */}
      {showVersions && id && (
        <VersionHistoryModal
          questionId={id}
          canEdit
          onClose={() => setShowVersions(false)}
        />
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* ── Basic Info ── */}
        <Section title="Basic Information">
          <div className={`grid grid-cols-2 ${(form.type === 'multiple_choice' || form.type === 'case_study') ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-4`}>
            <SelectField label="Type" value={form.type || 'multiple_choice'} options={TYPE_OPTIONS.map((t) => ({ value: t.value, label: t.label }))} onChange={(v) => updateField('type', v)} />
            {(form.type === 'multiple_choice' || form.type === 'case_study') && (
              <SelectField
                label="MCQ Format"
                value={form.mcqFormat || 'anbima'}
                options={MCQ_FORMAT_OPTIONS}
                onChange={(v) => updateField('mcqFormat', v)}
              />
            )}
            <SelectField label="Difficulty" value={form.difficulty || ''} options={DIFFICULTY_OPTIONS.map((d) => ({ value: d, label: d.charAt(0).toUpperCase() + d.slice(1) }))} onChange={(v) => updateField('difficulty', v)} />
            <SelectField label="Status" value={form.status || 'generated'} options={STATUS_OPTIONS.map((s) => ({ value: s, label: s.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase()) }))} onChange={(v) => updateField('status', v)} />
            <SelectField label="Cognitive Level" value={form.cognitiveLevel || ''} options={[{ value: '', label: 'Not set' }, ...COGNITIVE_OPTIONS.map((c) => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) }))]} onChange={(v) => updateField('cognitiveLevel', v)} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5">OE Code</label>
              <div className="input-field w-full text-sm py-2 bg-gray-50 text-gray-700 cursor-not-allowed font-mono">
                {form.oeCode || '-'}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5">Est. Time (sec)</label>
              <input
                type="number"
                value={form.estimatedTime || ''}
                onChange={(e) => updateField('estimatedTime', parseInt(e.target.value) || 0)}
                className="input-field w-full text-sm py-2"
                min={0}
              />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.hasCalculation || false}
                  onChange={(e) => updateField('hasCalculation', e.target.checked)}
                  className="w-4 h-4 text-[#1e73be] rounded"
                />
                <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                  <TrendingUp className="w-3.5 h-3.5" /> Has Calculation
                </span>
              </label>
            </div>
          </div>
        </Section>

        {/* ── Case Study Fields ── */}
        {form.type === 'case_study' && (
          <Section title="Case Study" icon={BookOpen} color="purple">
            <TextField label="Case Title" value={form.caseTitle || ''} onChange={(v) => updateField('caseTitle', v)} placeholder="e.g. Análise de Investimentos TechNova" />
            <div className="mt-3">
              <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5">Case Context</label>
              <RichTextEditor
                value={toEditorHtml(form.caseContext)}
                onChange={(html) => updateField('caseContext', html)}
              />
            </div>
            {form.caseData && Object.keys(form.caseData).length > 0 && (
              <div className="mt-3">
                <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5">Case Data (JSON)</label>
                <textarea
                  value={JSON.stringify(form.caseData, null, 2)}
                  onChange={(e) => {
                    try {
                    try { updateField('caseData', JSON.parse(e.target.value)); } catch {}
                    } catch (parseError) {
                      console.error("JSON parse failed:", parseError);
                      throw parseError;
                    }
                  }}
                  className="input-field w-full text-xs font-mono"
                  rows={6}
                />
              </div>
            )}
          </Section>
        )}

        {/* ── Case Study Sub-Questions ── */}
        {form.type === 'case_study' && (
          <Section title="Sub-Questions (3-5 MCQ Questions)" icon={BookOpen} color="purple">
            <p className="text-xs text-gray-500 mb-4">
              Each case study must have 3-5 multiple choice sub-questions based on the same context.
            </p>

            <div className="space-y-4">
              {(form.subQuestions || []).map((sq: any, sqIdx: number) => (
                <div key={sqIdx} className="bg-gradient-to-br from-purple-50 to-indigo-50/30 rounded-xl border-2 border-purple-200 p-4 shadow-sm">
                  {/* Sub-Question Header */}
                  <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-purple-200">
                    <span className="flex-shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-md">
                      {sq.caseQuestionNumber || sqIdx + 1}
                    </span>
                    <div className="flex-1">
                      <h4 className="text-sm font-bold text-purple-900">Question {sq.caseQuestionNumber || sqIdx + 1}</h4>
                      <p className="text-xs text-purple-600">Multiple Choice</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const newSqs = (form.subQuestions || []).filter((_: any, i: number) => i !== sqIdx);
                        updateField('subQuestions', newSqs);
                      }}
                      className="p-2 rounded-lg hover:bg-red-100 text-red-500 transition-colors"
                      title="Delete sub-question"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Command/Question Text */}
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5">
                      Question Command <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={sq.comando || ''}
                      onChange={(e) => {
                        const newSqs = [...(form.subQuestions || [])];
                        newSqs[sqIdx] = { ...sq, comando: e.target.value };
                        updateField('subQuestions', newSqs);
                      }}
                      className="input-field w-full text-sm"
                      rows={2}
                      placeholder="Considerando a situação apresentada..."
                      required
                    />
                  </div>

                  {/* Alternatives */}
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                      Alternatives (4 options)
                    </label>
                    {(sq.alternatives || []).map((alt: any, altIdx: number) => (
                      <div
                        key={altIdx}
                        className={`rounded-lg border-2 p-3 ${
                          alt.isCorrect ? 'border-green-300 bg-green-50/70' : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <input
                            type="radio"
                            name={`sq-${sqIdx}-correct`}
                            checked={alt.isCorrect || false}
                            onChange={() => {
                              const newSqs = [...(form.subQuestions || [])];
                              newSqs[sqIdx].alternatives = (sq.alternatives || []).map((a: any, i: number) => ({
                                ...a,
                                isCorrect: i === altIdx,
                              }));
                              newSqs[sqIdx].correctAnswer = alt.letter;
                              updateField('subQuestions', newSqs);
                            }}
                            className="w-4 h-4 text-green-600 mt-1"
                          />
                          <span className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${
                            alt.isCorrect ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {alt.letter}
                          </span>
                          <textarea
                            value={alt.text || ''}
                            onChange={(e) => {
                              const newSqs = [...(form.subQuestions || [])];
                              newSqs[sqIdx].alternatives[altIdx].text = e.target.value;
                              updateField('subQuestions', newSqs);
                            }}
                            className="flex-1 input-field text-sm"
                            rows={2}
                            placeholder={`Alternative ${alt.letter}`}
                            required
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Explanation */}
                  <div className="mt-4">
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5">
                      Explanation
                    </label>
                    <textarea
                      value={sq.explanation || ''}
                      onChange={(e) => {
                        const newSqs = [...(form.subQuestions || [])];
                        newSqs[sqIdx].explanation = e.target.value;
                        updateField('subQuestions', newSqs);
                      }}
                      className="input-field w-full text-sm"
                      rows={3}
                      placeholder="Explain why the correct answer is correct..."
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Add Sub-Question Button */}
            {(!form.subQuestions || form.subQuestions.length < 5) && (
              <button
                type="button"
                onClick={() => {
                  const newSq = {
                    caseQuestionNumber: (form.subQuestions || []).length + 1,
                    type: 'multiple_choice',
                    comando: '',
                    alternatives: [
                      { letter: 'A', text: '', isCorrect: false },
                      { letter: 'B', text: '', isCorrect: false },
                      { letter: 'C', text: '', isCorrect: false },
                      { letter: 'D', text: '', isCorrect: false },
                    ],
                    correctAnswer: '',
                    explanation: '',
                  };
                  updateField('subQuestions', [...(form.subQuestions || []), newSq]);
                }}
                className="w-full mt-3 py-3 px-4 rounded-xl border-2 border-dashed border-purple-300 bg-purple-50/50 hover:bg-purple-100/70 text-purple-700 font-semibold text-sm transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Sub-Question ({(form.subQuestions || []).length}/5)
              </button>
            )}
          </Section>
        )}

        {/* ── Interactive Fields ── */}
        {form.type === 'interactive' && (
          <Section title="Interactive" icon={MessageSquare} color="orange">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5">Initial Context</label>
              <RichTextEditor
                value={toEditorHtml(form.initialContext)}
                onChange={(html) => updateField('initialContext', html)}
              />
            </div>
            {/* Interactive Prompts Editor */}
            {form.prompts?.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-gray-600 uppercase">Decision Steps ({form.prompts.length})</label>
                </div>
                <div className="space-y-3">
                  {form.prompts.map((prompt: any, idx: number) => (
                    <InteractivePromptEditor
                      key={prompt.id || idx}
                      prompt={prompt}
                      index={idx}
                      onChange={(updated) => {
                        const newPrompts = [...form.prompts];
                        newPrompts[idx] = updated;
                        updateField('prompts', newPrompts);
                      }}
                      onRemove={() => {
                        const newPrompts = form.prompts.filter((_: any, i: number) => i !== idx);
                        updateField('prompts', newPrompts);
                      }}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const newId = `p${(form.prompts?.length || 0) + 1}`;
                    updateField('prompts', [...(form.prompts || []), { id: newId, text: '', role: 'client', alternatives: [] }]);
                  }}
                  className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700"
                >
                  <Plus className="w-3.5 h-3.5" /> Add Step
                </button>
              </div>
            )}
            {/* Optimal Path */}
            {form.optimalPath?.length > 0 && (
              <div className="mt-3">
                <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5">Optimal Path (comma-separated IDs)</label>
                <input
                  type="text"
                  value={(form.optimalPath || []).join(', ')}
                  onChange={(e) => updateField('optimalPath', e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean))}
                  className="input-field w-full text-sm py-2"
                  placeholder="p1, p2, p3"
                />
              </div>
            )}
          </Section>
        )}

        {/* ── Question Stem (hidden for case_study) ── */}
        {form.type !== 'case_study' && (
          <Section title="Question Content">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5">Stem / Question Text <span className="text-red-500">*</span></label>
              <RichTextEditor
                value={toEditorHtml(form.stem)}
                onChange={(html) => updateField('stem', html)}
              />
            </div>
          </Section>
        )}

        {/* ── MCQ Alternatives (not for case_study - those are in sub-questions) ── */}
        {form.type === 'multiple_choice' && (
          <Section title="Alternatives">
            <div className="space-y-3">
              {(form.alternatives || []).map((alt: any, i: number) => (
                <div key={i} className={`rounded-xl border-2 p-3 ${alt.isCorrect ? 'border-green-300 bg-green-50/50' : 'border-gray-100 bg-white'}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="radio"
                        name="correctAlt"
                        checked={alt.isCorrect || false}
                        onChange={() => {
                          const newAlts = form.alternatives.map((a: any, j: number) => ({
                            ...a,
                            isCorrect: j === i,
                          }));
                          updateField('alternatives', newAlts);
                          updateField('correctAnswer', alt.letter);
                        }}
                        className="w-4 h-4 text-green-600"
                      />
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${alt.isCorrect ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                        {alt.letter}
                      </span>
                    </div>
                    <div className="flex-1 space-y-2">
                      <RichTextEditor
                        compact
                        value={toEditorHtml(alt.text)}
                        onChange={(html) => {
                          const newAlts = [...form.alternatives];
                          newAlts[i] = { ...newAlts[i], text: html };
                          updateField('alternatives', newAlts);
                        }}
                      />
                      {/* {!alt.isCorrect && (
                        <input
                          type="text"
                          value={alt.whyWrong || ''}
                          onChange={(e) => {
                            const newAlts = [...form.alternatives];
                            newAlts[i] = { ...newAlts[i], whyWrong: e.target.value };
                            updateField('alternatives', newAlts);
                          }}
                          className="input-field w-full text-xs py-1.5 text-red-600"
                          placeholder="Why wrong (optional)"
                        />
                      )} */}
                      {/* {alt.isCorrect && (
                        <input
                          type="text"
                          value={alt.explanation || ''}
                          onChange={(e) => {
                            const newAlts = [...form.alternatives];
                            newAlts[i] = { ...newAlts[i], explanation: e.target.value };
                            updateField('alternatives', newAlts);
                          }}
                          className="input-field w-full text-xs py-1.5 text-green-600"
                          placeholder="Explanation for correct answer (optional)"
                        />
                      )} */}
                    </div>
                    {form.alternatives.length > 2 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newAlts = form.alternatives.filter((_: any, j: number) => j !== i);
                          updateField('alternatives', newAlts);
                        }}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg mt-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {(form.alternatives?.length || 0) < 6 && (
              <button
                type="button"
                onClick={() => {
                  const nextLetter = String.fromCharCode(65 + (form.alternatives?.length || 0));
                  updateField('alternatives', [...(form.alternatives || []), { letter: nextLetter, text: '', isCorrect: false, whyWrong: '', explanation: '' }]);
                }}
                className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-[#1e73be] hover:text-blue-700"
              >
                <Plus className="w-3.5 h-3.5" /> Add Alternative
              </button>
            )}
          </Section>
        )}

        {/* ── Short Answer Rubric ── */}
        {form.type === 'short_answer' && (
          <Section title="Rubric & Answer" color="amber">
            <TextField label="Expected Answer" value={form.correctAnswer || ''} onChange={(v) => updateField('correctAnswer', v)} placeholder="The correct answer text" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              <div>
                <label className="block text-xs font-semibold text-green-700 uppercase mb-1.5">Full Marks</label>
                <textarea
                  value={form.rubric?.fullMarks || ''}
                  onChange={(e) => updateField('rubric', { ...form.rubric, fullMarks: e.target.value })}
                  className="input-field w-full text-xs"
                  rows={3}
                  placeholder="Criteria for full marks..."
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-amber-700 uppercase mb-1.5">Partial Credit</label>
                <textarea
                  value={form.rubric?.partial || ''}
                  onChange={(e) => updateField('rubric', { ...form.rubric, partial: e.target.value })}
                  className="input-field w-full text-xs"
                  rows={3}
                  placeholder="Criteria for partial credit..."
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-red-700 uppercase mb-1.5">Zero</label>
                <textarea
                  value={form.rubric?.zero || ''}
                  onChange={(e) => updateField('rubric', { ...form.rubric, zero: e.target.value })}
                  className="input-field w-full text-xs"
                  rows={3}
                  placeholder="Criteria for zero marks..."
                />
              </div>
            </div>
          </Section>
        )}

        {/* ── Explanation (hidden for case_study - explanations are in sub-questions) ── */}
        {form.type !== 'case_study' && (
          <Section title="Explanation">
            <RichTextEditor
              value={toEditorHtml(form.explanation)}
              onChange={(html) => updateField('explanation', html)}
            />
          </Section>
        )}

        {/* ── Keywords & Tags ── */}
        <Section title="Classification">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ChipEditor
              label="Keywords"
              icon={Hash}
              items={form.keywords || []}
              onChange={(items) => updateField('keywords', items)}
              placeholder="Add keyword..."
              chipColor="bg-gray-100 text-gray-700"
            />
            <ChipEditor
              label="Tags"
              icon={Tag}
              items={form.tags || []}
              onChange={(items) => updateField('tags', items)}
              placeholder="Add tag..."
              chipColor="bg-teal-50 text-teal-700"
            />
          </div>
          {/* Behavioral Skills */}
          {form.behavioralSkills?.length > 0 && (
            <div className="mt-4">
              <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">Behavioral Skills</label>
              <div className="flex flex-wrap gap-2">
                {form.behavioralSkills.map((bs: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-50 border border-violet-200">
                    <Star className="w-3 h-3 text-violet-600" />
                    <input
                      type="text"
                      value={bs.skill || ''}
                      onChange={(e) => {
                        const updated = [...form.behavioralSkills];
                        updated[i] = { ...updated[i], skill: e.target.value };
                        updateField('behavioralSkills', updated);
                      }}
                      className="text-xs font-medium text-violet-700 bg-transparent border-none outline-none w-24"
                    />
                    <select
                      value={bs.level || 1}
                      onChange={(e) => {
                        const updated = [...form.behavioralSkills];
                        updated[i] = { ...updated[i], level: parseInt(e.target.value) };
                        updateField('behavioralSkills', updated);
                      }}
                      className="text-[10px] font-bold text-violet-500 bg-transparent border-none"
                    >
                      {[1, 2, 3, 4].map((l) => <option key={l} value={l}>L{l}</option>)}
                    </select>
                    <button type="button" onClick={() => updateField('behavioralSkills', form.behavioralSkills.filter((_: any, j: number) => j !== i))} className="text-violet-400 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* ── Source Info (read-only) ── */}
        {form.sourceDocuments?.length > 0 && (
          <Section title="Source">
            <div className="flex flex-wrap gap-2">
              {form.sourceDocuments.map((s: any, i: number) => (
                <span key={i} className="flex items-center gap-1.5 text-xs bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                  <FileText className="w-3 h-3 text-gray-400" />
                  {s.title || s.filename}
                </span>
              ))}
            </div>
            {form.generatedBy && (
              <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                <Zap className="w-3 h-3" /> Generated by: {form.generatedBy}
              </p>
            )}
          </Section>
        )}
      </form>
    </div>
  );
}

// ── Helper Components ───────────────────────────────────────────────────────

function Section({ title, icon: Icon, color, children }: { title: string; icon?: React.ElementType; color?: string; children: React.ReactNode }) {
  const colorMap: Record<string, string> = {
    purple: 'border-l-purple-400',
    orange: 'border-l-orange-400',
    amber: 'border-l-amber-400',
  };
  const borderClass = color ? colorMap[color] || '' : 'border-l-[#1e73be]';

  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-5 border-l-4 ${borderClass}`}>
      <h2 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" />}
        {title}
      </h2>
      {children}
    </div>
  );
}

function TextField({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-field w-full text-sm py-2"
        placeholder={placeholder}
      />
    </div>
  );
}

function SelectField({ label, value, options, onChange }: { label: string; value: string; options: Array<{ value: string; label: string }>; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="input-field w-full text-sm py-2">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function ChipEditor({ label, icon: Icon, items, onChange, placeholder, chipColor }: {
  label: string;
  icon: React.ElementType;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder: string;
  chipColor: string;
}) {
  const [input, setInput] = useState('');

  const add = () => {
    const val = input.trim();
    if (val && !items.includes(val)) {
      onChange([...items, val]);
      setInput('');
    }
  };

  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5 flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {items.map((item, i) => (
          <span key={i} className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${chipColor}`}>
            {item}
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500">
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          className="input-field flex-1 text-xs py-1.5"
          placeholder={placeholder}
        />
        <button type="button" onClick={add} className="px-2 py-1 text-xs font-semibold text-[#1e73be] hover:bg-blue-50 rounded-lg">
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function InteractivePromptEditor({ prompt, index, onChange, onRemove }: {
  prompt: any;
  index: number;
  onChange: (updated: any) => void;
  onRemove: () => void;
}) {
  const roleColors = prompt.role === 'client'
    ? 'border-orange-200 bg-orange-50/50'
    : 'border-blue-200 bg-blue-50/50';

  return (
    <div className={`rounded-xl border-2 p-3 ${roleColors}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase text-gray-500">Step {index + 1}</span>
          <select
            value={prompt.role || 'client'}
            onChange={(e) => onChange({ ...prompt, role: e.target.value })}
            className="text-[10px] font-bold bg-transparent border-none text-gray-600"
          >
            <option value="client">Client</option>
            <option value="system">System</option>
          </select>
        </div>
        <button type="button" onClick={onRemove} className="text-red-400 hover:text-red-600">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <textarea
        value={prompt.text || ''}
        onChange={(e) => onChange({ ...prompt, text: e.target.value })}
        className="input-field w-full text-xs mb-2"
        rows={2}
        placeholder="Prompt text..."
      />
      {/* Alternatives */}
      <div className="space-y-1.5 ml-3 pl-3 border-l-2 border-gray-200">
        {(prompt.alternatives || []).map((alt: any, ai: number) => (
          <div key={ai} className="flex items-center gap-2">
            <span className="w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold bg-gray-100 text-gray-600">{alt.letter}</span>
            <input
              type="text"
              value={alt.text || ''}
              onChange={(e) => {
                const newAlts = [...prompt.alternatives];
                newAlts[ai] = { ...newAlts[ai], text: e.target.value };
                onChange({ ...prompt, alternatives: newAlts });
              }}
              className="input-field flex-1 text-xs py-1"
              placeholder={`Option ${alt.letter}`}
            />
            <label className="flex items-center gap-1 text-[10px]">
              <input
                type="checkbox"
                checked={alt.correct || false}
                onChange={(e) => {
                  const newAlts = [...prompt.alternatives];
                  newAlts[ai] = { ...newAlts[ai], correct: e.target.checked };
                  onChange({ ...prompt, alternatives: newAlts });
                }}
                className="w-3 h-3"
              />
              <span className="text-green-600 font-semibold">Correct</span>
            </label>
            <input
              type="number"
              value={alt.score ?? ''}
              onChange={(e) => {
                const newAlts = [...prompt.alternatives];
                newAlts[ai] = { ...newAlts[ai], score: parseInt(e.target.value) || 0 };
                onChange({ ...prompt, alternatives: newAlts });
              }}
              className="input-field w-12 text-[10px] py-1 text-center"
              placeholder="Pt"
              min={0}
              max={5}
            />
            <button
              type="button"
              onClick={() => {
                const newAlts = prompt.alternatives.filter((_: any, j: number) => j !== ai);
                onChange({ ...prompt, alternatives: newAlts });
              }}
              className="text-red-400 hover:text-red-600"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => {
            const nextLetter = String.fromCharCode(65 + (prompt.alternatives?.length || 0));
            onChange({ ...prompt, alternatives: [...(prompt.alternatives || []), { letter: nextLetter, text: '', correct: false, score: 0 }] });
          }}
          className="text-[10px] font-semibold text-orange-600 hover:text-orange-700 flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> Add Option
        </button>
      </div>
    </div>
  );
}

export default PdfQuestionEdit;
